import React from 'react';
import P, { InferProps } from 'prop-types';
import { SxStyle } from '../../../types';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import Card from '@mui/material/Card';
import CardBackgroundImg from './components/card-background-img';
import Module from './module';
import MobileDevice from '../../common/mobile-device';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { useUserAuth } from '../../../contexts/auth';
import ColumnHeader from './column-header';
import ColumnContent from './column-content';
import { Box, Button, Drawer, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import FormControlLabel from '@mui/material/FormControlLabel';
import DrawerHeader from '../../common/drawer-header';

const styles: SxStyle = {
  card: {
    '& .MuiCardHeader-root': {
      padding: '10px',
    },
    margin: '8px',
  },
  root: {
    flexShrink: 0,
  },
  containerDragging: (theme) => ({
    border: '1px dashed',
    borderColor: theme.palette.primary.main,
    borderRadius: 8,
  }),
  lastColumn: {
    flex: '1 1 auto',
  },
};

function DeviceColumn({
  modules,
  setChange,
  setValidation,
  showPreview,
  togglePreview,
  onDelete,
  selectedDevices,
  isReadOnly,
  pathParams,
  promos,
  has3Columns,
  // has2Columns,
  has1Column,
  // openStaticModulesDrawer,
  setOpenStaticModulesDrawer,
}: InferProps<typeof DeviceColumn.propTypes>) {
  const { isSuperAdmin } = useUserAuth();

  const [openPreviewDrawer, setOpenPreviewDrawer] = React.useState(false);

  const getStyle = (isDragging: boolean, draggableStyle: any) => ({
    // styles we need to apply on draggables
    ...draggableStyle,
    backgroundColor: 'transparent',
    ...(isDragging && {
      border: '1px dashed #000',
      backgroundColor: '#fff',
    }),
  });

  if (!promos) {
    return null;
  }

  const device = Object.keys(modules).find((device) => device === selectedDevices);

  if (!device) {
    return null;
  }
  return (
    <>
      <Box sx={{ ...(!has3Columns ? styles.lastColumn : {}) }}>
        <ColumnHeader title="Valda moduler" />
        <ColumnContent>
          <MobileDevice
            device={device}
            displayDevice
            header={
              !has3Columns ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => {
                      setOpenPreviewDrawer(true);
                    }}
                    startIcon={<VisibilityIcon />}
                  >
                    Förhandsgranskning
                  </Button>
                  {has1Column ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        setOpenStaticModulesDrawer(true);
                      }}
                      startIcon={<AddIcon />}
                    >
                      Tillgängliga moduler
                    </Button>
                  ) : null}
                </Box>
              ) : null
            }
          >
            <Droppable droppableId={device} isDropDisabled={isReadOnly}>
              {(provided, snapshot) => (
                <Stack
                  style={{ margin: '8px' }}
                  ref={provided.innerRef}
                  sx={snapshot.isDraggingOver ? styles.containerDragging : {}}
                >
                  {modules[device].map((item: any, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                      isDragDisabled={isReadOnly}
                    >
                      {(provided, snapshot) => (
                        <>
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={getStyle(snapshot.isDragging, provided.draggableProps.style)}
                            sx={styles.card}
                            elevation={2}
                          >
                            <Module
                              key={item.id}
                              item={item}
                              setChange={setChange}
                              setValidation={setValidation}
                              currentKey={device}
                              index={index}
                              path={`${device}[${index}]`}
                              onDelete={onDelete}
                              pathParams={pathParams}
                              isReadOnly={isReadOnly}
                              isNonRemoveable={!!(item.isNonRemoveable && !isSuperAdmin)}
                              promos={promos}
                            />
                          </Card>
                        </>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </MobileDevice>
        </ColumnContent>
      </Box>
      {has3Columns ? (
        <Box sx={styles.lastColumn}>
          <ColumnHeader>
            <FormControlLabel
              control={
                <Switch
                  checked={showPreview}
                  onChange={(event, checked) => togglePreview(checked)}
                  color="primary"
                />
              }
              sx={{ fontWeight: 'bold' }}
              label={
                <Typography
                  variant="body2"
                  sx={{
                    flex: '1 1 auto',
                  }}
                >
                  <strong>Förhandsgranskning</strong>
                </Typography>
              }
            />
          </ColumnHeader>
          <ColumnContent>
            <MobileDevice
              device={device}
              displayDevice={
                showPreview && (selectedDevices === 'both' || selectedDevices === device)
              }
            >
              {modules[device].map((module: any) => (
                <CardBackgroundImg key={module.id} sceneModule={module} promos={promos} />
              ))}
            </MobileDevice>
          </ColumnContent>
        </Box>
      ) : (
        <Drawer
          open={openPreviewDrawer}
          onClose={() => {
            setOpenPreviewDrawer(false);
          }}
          anchor="right"
        >
          <DrawerHeader
            title="Förhandsgranskning"
            onClose={() => {
              setOpenPreviewDrawer(false);
            }}
          />
          <Box sx={{ height: 'calc(100vh - 50px)', px: 2 }}>
            <MobileDevice device={device} displayDevice>
              {modules[device].map((module: any) => (
                <CardBackgroundImg key={module.id} sceneModule={module} promos={promos} />
              ))}
            </MobileDevice>
          </Box>
        </Drawer>
      )}
    </>
  );
}

DeviceColumn.propTypes = {
  modules: P.objectOf(P.arrayOf(P.object).isRequired).isRequired,
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  showPreview: P.bool.isRequired,
  togglePreview: P.func.isRequired,
  onDelete: P.func.isRequired,
  selectedDevices: P.string.isRequired,
  isReadOnly: P.bool.isRequired,
  pathParams: P.string.isRequired,
  promos: P.arrayOf(P.object),
  has3Columns: P.bool.isRequired,
  has2Columns: P.bool.isRequired,
  has1Column: P.bool.isRequired,
  openStaticModulesDrawer: P.bool.isRequired,
  setOpenStaticModulesDrawer: P.func.isRequired,
};

export default DeviceColumn;
